// Vue tableau du schéma : équipements et liaisons entièrement éditables sans le dessin (vue par défaut sur téléphone).
import { useEffect, useState } from 'react';
import { Link2, Settings2, Trash2 } from 'lucide-react';
import { Bouton, EtatVide } from '../../components/ui.tsx';
import { BadgeCompatibilite } from './ProprietesSchema.tsx';
import { LIBELLE_NOEUD, TYPES_LIAISON, TYPES_NOEUD, compatibiliteEffective, type Donnees, type Liaison, type Noeud, type TypeLiaison } from './modele.ts';
import type { Selection } from './CanevasSchema.tsx';

interface Props {
  donnees: Donnees;
  selection: Selection;
  lectureSeule: boolean;
  majNoeud: (id: string, champ: Partial<Noeud>, cle: string) => void;
  majLiaison: (id: string, champ: Partial<Liaison>, cle: string) => void;
  supprimer: (s: NonNullable<Selection>) => void;
  ouvrir: (s: NonNullable<Selection>) => void;
}

/** Protocoles saisis en texte (« KNX, BACnet/IP ») : la liste n'est appliquée qu'à la sortie du champ. */
function ChampProtocoles({ n, desactive, maj }: { n: Noeud; desactive: boolean; maj: (p: string[]) => void }) {
  const [v, setV] = useState(n.protocoles.join(', '));
  useEffect(() => { setV(n.protocoles.join(', ')); }, [n.protocoles]);
  const appliquer = () => {
    const liste = [...new Set(v.split(/[,;]/).map((x) => x.trim()).filter(Boolean))].slice(0, 12);
    if (liste.join('|') !== n.protocoles.join('|')) maj(liste);
  };
  return <input className="saisie" value={v} disabled={desactive} onChange={(e) => setV(e.target.value)} onBlur={appliquer}
    onKeyDown={(e) => { if (e.key === 'Enter') appliquer(); }} aria-label={`Protocoles de « ${n.label} » (séparés par des virgules)`} placeholder="KNX, BACnet/IP" />;
}

export function TableauSchema({ donnees, selection, lectureSeule, majNoeud, majLiaison, supprimer, ouvrir }: Props) {
  const nom = (id: string) => donnees.nodes.find((n) => n.id === id)?.label ?? '';
  return (
    <div className="pile-l">
      <section className="pile" style={{ gap: 8 }} aria-labelledby="cch-t-noeuds">
        <h3 id="cch-t-noeuds" className="cch-titre-tableau">Équipements <span className="compteur">{donnees.nodes.length}</span></h3>
        {!donnees.nodes.length ? <EtatVide titre="Aucun équipement" texte="Ajoutez un automate, une passerelle, des capteurs… avec le bouton « Ajouter »." /> : (
          <div className="tableau-conteneur">
            <table className="tableau cch-tableau">
              <thead><tr><th>Désignation</th><th>Type</th><th>Fabricant</th><th>Référence</th><th>Zone</th><th>Protocoles déclarés</th><th><span className="visuellement-cache">Actions</span></th></tr></thead>
              <tbody>
                {donnees.nodes.map((n) => (
                  <tr key={n.id} className={selection?.type === 'noeud' && selection.id === n.id ? 'est-choisi' : undefined}>
                    <td data-libelle="Désignation"><input className="saisie" value={n.label} disabled={lectureSeule} maxLength={120} aria-label="Désignation"
                      onChange={(e) => majNoeud(n.id, { label: e.target.value }, `noeud-${n.id}-label`)} /></td>
                    <td data-libelle="Type"><select className="saisie" value={n.type} disabled={lectureSeule} aria-label={`Type de « ${n.label} »`}
                      onChange={(e) => majNoeud(n.id, { type: e.target.value as Noeud['type'] }, `noeud-${n.id}-type-${Date.now()}`)}>
                      {TYPES_NOEUD.map((t) => <option key={t.id} value={t.id}>{t.libelle}</option>)}</select></td>
                    <td data-libelle="Fabricant"><input className="saisie" value={n.fabricant ?? ''} disabled={lectureSeule} maxLength={120} aria-label={`Fabricant de « ${n.label} »`}
                      onChange={(e) => majNoeud(n.id, { fabricant: e.target.value || null }, `noeud-${n.id}-fabricant`)} /></td>
                    <td data-libelle="Référence"><input className="saisie saisie-mono" value={n.reference ?? ''} disabled={lectureSeule} maxLength={120} aria-label={`Référence de « ${n.label} »`}
                      onChange={(e) => majNoeud(n.id, { reference: e.target.value || null }, `noeud-${n.id}-reference`)} /></td>
                    <td data-libelle="Zone"><input className="saisie" value={n.zone ?? ''} disabled={lectureSeule} maxLength={120} aria-label={`Zone de « ${n.label} »`}
                      onChange={(e) => majNoeud(n.id, { zone: e.target.value || null }, `noeud-${n.id}-zone`)} /></td>
                    <td data-libelle="Protocoles" className="cch-large"><ChampProtocoles n={n} desactive={lectureSeule}
                      maj={(p) => majNoeud(n.id, { protocoles: p }, `noeud-${n.id}-proto-${Date.now()}`)} /></td>
                    <td className="cch-actions">
                      <Bouton compact variante="fantome" icone={<Settings2 aria-hidden />} onClick={() => ouvrir({ type: 'noeud', id: n.id })}
                        aria-label={`Propriétés et liaisons de « ${n.label} »`}>{lectureSeule ? 'Détails' : 'Relier, détails'}</Bouton>
                      {!lectureSeule && <Bouton compact variante="fantome" className="btn-icone" icone={<Trash2 aria-hidden />}
                        onClick={() => supprimer({ type: 'noeud', id: n.id })} aria-label={`Supprimer « ${n.label} »`} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="pile" style={{ gap: 8 }} aria-labelledby="cch-t-liaisons">
        <h3 id="cch-t-liaisons" className="cch-titre-tableau">Liaisons <span className="compteur">{donnees.links.length}</span></h3>
        {!donnees.links.length ? (
          <EtatVide icone={<Link2 />} titre="Aucune liaison"
            texte={donnees.nodes.length < 2 ? 'Il faut au moins deux équipements pour créer une liaison.' : 'Ouvrez « Relier, détails » sur un équipement, puis « Relier à… ».'} />
        ) : (
          <div className="tableau-conteneur">
            <table className="tableau cch-tableau">
              <thead><tr><th>Depuis</th><th>Vers</th><th>Type</th><th>Libellé</th><th>Compatibilité</th><th><span className="visuellement-cache">Actions</span></th></tr></thead>
              <tbody>
                {donnees.links.map((l) => {
                  const eff = compatibiliteEffective(l, donnees.nodes);
                  return (
                    <tr key={l.id} className={selection?.type === 'liaison' && selection.id === l.id ? 'est-choisi' : undefined}>
                      <td data-libelle="Depuis"><select className="saisie" value={l.de} disabled={lectureSeule} aria-label="Équipement de départ"
                        onChange={(e) => e.target.value !== l.vers && majLiaison(l.id, { de: e.target.value }, `liaison-${l.id}-de-${Date.now()}`)}>
                        {donnees.nodes.map((n) => <option key={n.id} value={n.id}>{n.label} ({LIBELLE_NOEUD[n.type]})</option>)}</select></td>
                      <td data-libelle="Vers"><select className="saisie" value={l.vers} disabled={lectureSeule} aria-label={`Équipement d’arrivée depuis « ${nom(l.de)} »`}
                        onChange={(e) => e.target.value !== l.de && majLiaison(l.id, { vers: e.target.value }, `liaison-${l.id}-vers-${Date.now()}`)}>
                        {donnees.nodes.map((n) => <option key={n.id} value={n.id}>{n.label} ({LIBELLE_NOEUD[n.type]})</option>)}</select></td>
                      <td data-libelle="Type"><span className="cch-type-cellule"><span className={`cch-pastille cch-l-${l.type}`} aria-hidden />
                        <select className="saisie" value={l.type} disabled={lectureSeule} aria-label={`Type de la liaison « ${nom(l.de)} » – « ${nom(l.vers)} »`}
                          onChange={(e) => majLiaison(l.id, { type: e.target.value as TypeLiaison }, `liaison-${l.id}-type-${Date.now()}`)}>
                          {TYPES_LIAISON.map((t) => <option key={t.id} value={t.id}>{t.libelle}</option>)}</select></span></td>
                      <td data-libelle="Libellé"><input className="saisie" value={l.libelle ?? ''} disabled={lectureSeule} maxLength={120} aria-label="Libellé de la liaison"
                        onChange={(e) => majLiaison(l.id, { libelle: e.target.value || null }, `liaison-${l.id}-libelle`)} /></td>
                      <td data-libelle="Compatibilité"><span className="pile" style={{ gap: 4, alignItems: 'flex-start' }}>
                        <BadgeCompatibilite etat={eff.etat} />
                        {eff.raison && l.compatibilite === 'etayee' && <span className="tres-petit cch-texte-ambre">{eff.raison}</span>}
                      </span></td>
                      <td className="cch-actions">
                        <Bouton compact variante="fantome" icone={<Settings2 aria-hidden />} onClick={() => ouvrir({ type: 'liaison', id: l.id })}
                          aria-label={`Source et détails de la liaison « ${nom(l.de)} » – « ${nom(l.vers)} »`}>{lectureSeule ? 'Détails' : 'Source, détails'}</Bouton>
                        {!lectureSeule && <Bouton compact variante="fantome" className="btn-icone" icone={<Trash2 aria-hidden />}
                          onClick={() => supprimer({ type: 'liaison', id: l.id })} aria-label={`Supprimer la liaison « ${nom(l.de)} » – « ${nom(l.vers)} »`} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
